import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { Outlet, useLocation } from "@tanstack/react-router";
import { Sidebar } from "@/components/layout/Sidebar";
import { Header } from "@/components/layout/Header";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { DockNavigation } from "@/components/layout/DockNavigation";
import FeedbackWidget from "@/components/shared/FeedbackWidget";
import { PrintPermissionGuard } from "@/components/shared/PrintPermissionGuard";
import { usePageMode, useIsSimpleCatalog } from "@/hooks/usePageMode";
import type { PageMode } from "@/hooks/usePageMode";
import { useAuthStore } from "@/stores/auth.store";
import { useSyncStore } from "@/stores/sync.store";
import { trpcClient } from "@/lib/trpc";

// Create Context for Sidebar State + Mobile Navigation Visibility
interface SidebarContextType {
    isCollapsed: boolean;
    toggleSidebar: () => void;
    isMobileOpen: boolean;
    setMobileOpen: (open: boolean) => void;
    // Mobile navigation visibility
    isHeaderVisible: boolean;
    isDockVisible: boolean;
    pageMode: PageMode;
}

const SidebarContext = createContext<SidebarContextType>({
    isCollapsed: false,
    toggleSidebar: () => { },
    isMobileOpen: false,
    setMobileOpen: () => { },
    isHeaderVisible: true,
    isDockVisible: true,
    pageMode: 'list',
});

export const useSidebar = () => useContext(SidebarContext);

export default function RootLayout() {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    const pageMode = usePageMode();
    const isSimpleCatalog = useIsSimpleCatalog();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const setUser = useAuthStore((s) => s.setUser);

    // Sync fresh user role & permissions from backend on mount and window focus
    useEffect(() => {
        if (!isAuthenticated) return;
        const syncProfile = () => {
            trpcClient.auth.me.query()
                .then((freshUser) => {
                    if (freshUser && freshUser.id) {
                        setUser(freshUser as any);
                    }
                })
                .catch(() => {});
        };
        syncProfile();
        window.addEventListener("focus", syncProfile);
        return () => window.removeEventListener("focus", syncProfile);
    }, [isAuthenticated, setUser]);

    // Mobile navigation visibility state
    const [isHeaderVisible, setHeaderVisible] = useState(true);
    const [isDockVisible, setDockVisible] = useState(true);
    const mainRef = useRef<HTMLElement>(null);
    const lastScrollY = useRef(0);
    const isSyncActive = useSyncStore((s) => s.status === 'SYNCING' || s.status === 'SUCCESS');

    // Mobile: auto-reveal Top Header when background sync / save occurs while scrolled down
    useEffect(() => {
        if (isSyncActive && typeof window !== 'undefined' && window.innerWidth < 768 && !isHeaderVisible) {
            setHeaderVisible(true);
        }
    }, [isSyncActive, isHeaderVisible]);

    const toggleSidebar = () => {
        if (window.innerWidth < 768) {
            setMobileOpen(!isMobileOpen);
        } else {
            setIsCollapsed(!isCollapsed);
        }
    };

    // Close mobile sidebar on route change
    useEffect(() => {
        setMobileOpen(false);
    }, [location.href]);



    // Mobile: auto-hide header/dock on form/view pages, scroll-based on list pages
    useEffect(() => {
        const isMobile = window.innerWidth < 768;

        if (!isMobile) {
            // Desktop: always show
            setHeaderVisible(true);
            setDockVisible(true);
            return;
        }

        if (pageMode === 'form' || pageMode === 'view') {
            // Form/View/Edit: Unified contextual header is the single source of truth; keep visible on mobile!
            setHeaderVisible(true);
            setDockVisible(false);
            return;
        }

        // List pages: show by default, then scroll-toggle
        setHeaderVisible(true);
        setDockVisible(true);
        lastScrollY.current = 0;
    }, [pageMode, location.pathname]);

    // Scroll listener on <main> for list pages on mobile
    const handleScroll = useCallback(() => {
        if (window.innerWidth >= 768) return; // Desktop: skip
        if (pageMode !== 'list') return; // Only list pages get scroll toggle

        const el = mainRef.current;
        if (!el) return;

        const currentScrollY = el.scrollTop;
        const threshold = 50;

        // Show if scrolling up or near top
        if (currentScrollY < lastScrollY.current || currentScrollY < threshold) {
            setHeaderVisible(true);
            setDockVisible(true);
        }
        // Hide if scrolling down past threshold
        else if (currentScrollY > lastScrollY.current && currentScrollY > threshold) {
            setHeaderVisible(false);
            setDockVisible(false);
        }

        lastScrollY.current = currentScrollY;
    }, [pageMode]);

    useEffect(() => {
        const el = mainRef.current;
        if (!el) return;
        if (pageMode !== 'list') return;
        if (window.innerWidth >= 768) return;

        let ticking = false;
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        el.addEventListener("scroll", onScroll, { passive: true });
        return () => el.removeEventListener("scroll", onScroll);
    }, [handleScroll, pageMode, location.pathname]);

    // Listen for window resize to reset visibility on desktop
    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth >= 768) {
                setHeaderVisible(true);
                setDockVisible(true);
            }
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const isLandingPage = location.pathname === '/landingpage' || location.pathname.startsWith('/landingpage/');

    if (isLandingPage) {
        return (
            <SidebarContext.Provider value={{
                isCollapsed, toggleSidebar, isMobileOpen, setMobileOpen,
                isHeaderVisible, isDockVisible, pageMode,
            }}>
                <div className="min-h-screen bg-white">
                    <Outlet />
                    <Toaster position="top-right" />
                </div>
            </SidebarContext.Provider>
        );
    }

    return (
        <SidebarContext.Provider value={{
            isCollapsed, toggleSidebar, isMobileOpen, setMobileOpen,
            isHeaderVisible, isDockVisible, pageMode,
        }}>
                <div className="flex h-screen w-screen overflow-hidden print:block print:h-auto print:w-full print:overflow-visible" style={{ backgroundColor: 'var(--apv-content-bg, #f4f6f9)' }}>

                    <style>{`
                        @media print {
                            nav, aside, header, .dock-nav, [data-testid="dock-navigation"], [data-testid="feedback-widget-trigger"], [data-testid="feedback-widget-modal"], [data-testid="floating-bulk-action-bar"] { display: none !important; }
                             /* Reset margins for main content when printing */
                            main { margin: 0 !important; padding: 0 !important; width: 100% !important; max-width: 100% !important; height: auto !important; overflow: visible !important; display: block !important; }
                             /* Ensure background colors print */
                            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                        }
                    `}</style>
                    {/* Sidebar - Desktop */}
                    <div
                        className={cn(
                            "hidden md:flex print:hidden flex-col fixed inset-y-0 z-50 transition-all duration-300 ease-in-out",
                            isCollapsed ? "w-[70px]" : "w-64"
                        )}
                    >
                        <div className="flex-1 flex flex-col h-full relative">
                            <Sidebar isCollapsed={isCollapsed} />
                        </div>
                    </div>

                    {/* Sidebar - Mobile */}
                    <div
                        className={cn(
                            "fixed inset-0 z-[60] md:hidden print:hidden bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300",
                            isMobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                        )}
                        onClick={() => setMobileOpen(false)}
                    />
                    <div
                        className={cn(
                            "fixed inset-y-0 left-0 z-[60] w-64 shadow-xl transform transition-transform duration-300 ease-in-out md:hidden print:hidden",
                            isMobileOpen ? "translate-x-0" : "-translate-x-full"
                        )}
                    >
                        <Sidebar isCollapsed={false} />
                    </div>

                    {/* Main Content */}
                    <div
                        className={cn(
                            "flex flex-1 flex-col h-screen overflow-hidden transition-all duration-300 ease-in-out print:block print:h-auto print:overflow-visible print:pl-0",
                            isCollapsed ? "md:pl-[70px] print:md:pl-0" : "md:pl-64 print:md:pl-0"
                        )}
                    >
                        <Header />
                        <main
                            ref={mainRef}
                            className={cn(
                                "apv-content-area flex-1 overflow-y-auto w-full scroll-smooth pb-[80px] md:pb-0 print:block print:h-auto print:overflow-visible print:pb-0",
                                // On mobile, when header is hidden, add top padding for safe area
                                !isHeaderVisible && "mobile-no-header",
                                // Simple catalog views/forms: popup-style on desktop
                                isSimpleCatalog && (pageMode === 'form' || pageMode === 'view') && "apv-popup-layout"
                            )}
                            style={{ backgroundColor: 'var(--apv-content-bg, #f4f6f9)' }}
                        >
                            <Outlet />
                        </main>
                    </div>
                    <Toaster position="top-right" />
                    {/* Quyền In theo bảng phân quyền cho mọi nút gọi window.print() */}
                    <PrintPermissionGuard />
                    <div className="print:hidden">
                        <FeedbackWidget />
                        <DockNavigation />
                    </div>
                </div>
            </SidebarContext.Provider>
    );
}
